/**
 * LinkedInOAuthCallbackPage — receives LinkedIn's OAuth redirect at
 * `/integrations/linkedin/callback`, extracts `?code=&state=`, POSTs
 * them to `/api/linkedin/oauth/exchange` (which resolves the client_id
 * from the OAuth state doc), then redirects the admin back to the
 * appropriate LinkedIn page.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function LinkedInOAuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("busy"); // busy | ok | err
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const code = params.get("code");
    const oauthState = params.get("state");
    const err = params.get("error");
    const errDesc = params.get("error_description");
    if (err) {
      setState("err");
      setMsg(errDesc || err);
      return;
    }
    if (!code || !oauthState) {
      setState("err");
      setMsg("Missing OAuth response parameters (code / state).");
      return;
    }
    (async () => {
      try {
        const redirectUri = `${window.location.origin}/integrations/linkedin/callback`;
        const { data } = await api.post("/linkedin/oauth/exchange", {
          state: oauthState, code, redirect_uri: redirectUri,
        });
        setState("ok");
        const label = data.app_type === "pages" ? "Community Management API"
                                                : "Ads app";
        setMsg(`${label} connected as ${data.member?.name || data.member?.email || "LinkedIn member"}.`);
        toast.success(`${label} connected`);
        // If we just completed the Ads-app flow, chain straight into
        // the Pages-app flow so the admin only clicks once (delivers the
        // requested "double OAuth flow"). If Pages is already connected
        // or Pages credentials aren't set up, we just return to the
        // client page.
        setTimeout(async () => {
          const cid = data.client_id;
          if (!cid) {
            navigate("/integrations/linkedin", { replace: true });
            return;
          }
          if (data.app_type === "ads") {
            try {
              const conn = await api.get(`/clients/${cid}/linkedin/connection`)
                .then((r) => r.data).catch(() => ({}));
              if (!conn.pages_connected) {
                setMsg("Ads app connected — chaining into Community Management…");
                const { data: pagesStart } = await api.post(
                  `/clients/${cid}/linkedin/oauth/start`,
                  { redirect_uri: redirectUri, app_type: "pages" },
                );
                if (pagesStart?.authorize_url) {
                  window.location.href = pagesStart.authorize_url;
                  return;
                }
              }
            } catch (chainErr) {
              // Non-fatal — the admin can start the Pages flow manually
              // from the client's LinkedIn page.
            }
          }
          navigate(`/clients/${cid}/linkedin`, { replace: true });
        }, 1200);
      } catch (e) {
        setState("err");
        setMsg(e?.response?.data?.detail || e?.message || "OAuth exchange failed.");
      }
    })();
  }, [params, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
      <Card className="max-w-md w-full rounded-sm" data-testid="linkedin-oauth-callback">
        <CardContent className="p-6 text-center space-y-3">
          {state === "busy" && (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-[#002FA7] mx-auto" />
              <div className="font-display font-bold text-lg">Finishing LinkedIn sign-in…</div>
              <p className="text-sm text-zinc-600">Exchanging the authorisation code for a token.</p>
            </>
          )}
          {state === "ok" && (
            <>
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="font-display font-bold text-lg">LinkedIn connected</div>
              <p className="text-sm text-zinc-600">{msg}</p>
              <p className="text-xs text-zinc-500">Redirecting back to your client…</p>
            </>
          )}
          {state === "err" && (
            <>
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="font-display font-bold text-lg">Connection failed</div>
              <p className="text-sm text-red-700 break-words">{msg}</p>
              <button className="mt-2 text-xs text-[#002FA7] hover:underline"
                onClick={() => navigate(-1)}
                data-testid="linkedin-oauth-back">← Back</button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
